import { Card, CardContent, Typography } from "@material-ui/core";
import { useParams } from "react-router";
import { reviewSessionsApi, wordsApi } from "../providers/env";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ReviewSession, WordWithDefinition } from "../generated";
import { CardActions, Link, useAuthState, useNotify } from "react-admin";
import { AxiosError } from "axios";
import { Button as MuiButton } from "@material-ui/core";

const centeredCard = { margin: "25px auto", maxWidth: 400, minWidth: 300 };
const useReviewSessionsAsync = (wordlistId: string) => {
  const [reviewSession, setReviewSession] = useState<ReviewSession | undefined>(
    undefined
  );
  useEffect(() => {
    async function fetchReviewSession() {
      const reviewSession = await reviewSessionsApi.getOrCreateReviewSession({
        wordlistId,
      });
      setReviewSession(reviewSession.data);
    }
    fetchReviewSession();
  }, [wordlistId]);

  return reviewSession;
};

async function fetchNextWord(
  setNextWord: Dispatch<SetStateAction<WordWithDefinition | undefined>>,
  setNextWordLoaded: Dispatch<SetStateAction<boolean>>,
  reviewSession: ReviewSession | undefined,
  notify: any
) {
  if (reviewSession === undefined) {
    return;
  }
  setNextWordLoaded(false);
  try {
    const word = await wordsApi.getNextWord({
      reviewSessionId: reviewSession.id,
    });
    setNextWord(word.data);
    setNextWordLoaded(true);
  } catch (e) {
    const exp = e as AxiosError;
    if (exp.response?.status === 404) {
      setNextWord(undefined);
      setNextWordLoaded(true);
    } else {
      notify("Network error", "error");
    }
  }
}

const useNextWordAsync = (reviewSession: ReviewSession | undefined) => {
  const [nextWord, setNextWord] = useState<WordWithDefinition | undefined>(
    undefined
  );
  const [nextWordLoaded, setNextWordLoaded] = useState<boolean>(false);
  const notify = useNotify();
  useEffect(() => {
    fetchNextWord(setNextWord, setNextWordLoaded, reviewSession, notify);
  }, [reviewSession, notify]);

  return { nextWord, setNextWord, nextWordLoaded, setNextWordLoaded };
};

const BackFlashcardContent = ({ ...props }) => {
  const { pronunciation, results } = props;
  return (
    <div>
      {pronunciation ? (
        <Typography component="div">/{pronunciation}/</Typography>
      ) : null}
      {results.length === 0 ? "Sorry, no definition found for this word" : ""}
      {results.map((definition: any, index: number) => (
        <div>
          <Typography>
            {results.length > 1 ? `${index + 1}. ` : ""}
            {definition.partOfSpeech}
          </Typography>
          <Typography variant="body2">
            {definition.definition ||
              "We're sorry! No definition found for this word."}
            <br />
            {definition.example}
          </Typography>
          <br />
        </div>
      ))}
    </div>
  );
};

export const NotLoggedInCTA = () => {
  return (
    <Card style={centeredCard} variant="outlined">
      <CardContent>
        Hi 👋, your progress is not being saved.{" "}
        <Link to="/register">Sign up</Link> to save your progress, create your
        own word lists and be the first to know when new features are added
      </CardContent>
    </Card>
  );
};

export const Practice = ({ ...props }) => {
  const { wordlistId } = useParams<{ wordlistId: string }>();
  const { authenticated, loaded: isAuthStateLoaded } = useAuthState();
  const notify = useNotify();
  const reviewSession = useReviewSessionsAsync(wordlistId);
  const [isReviewing, setIsReviewing] = useState(true);
  const { nextWord, setNextWord, nextWordLoaded, setNextWordLoaded } =
    useNextWordAsync(reviewSession);
  if (reviewSession === undefined) {
    return <div>Loading</div>;
  }
  const rateNextWord = (quality: number) => {
    setIsReviewing(false);
    wordsApi.reviewWord({ reviewSessionId: reviewSession.id, quality });
  };
  if (nextWordLoaded !== true) {
    return (
      <Card>
        {isAuthStateLoaded && !authenticated ? <NotLoggedInCTA /> : null}
        <CardContent>Loading</CardContent>
      </Card>
    );
  }
  if (nextWord === undefined) {
    return (
      <Card>
        {isAuthStateLoaded && !authenticated ? <NotLoggedInCTA /> : null}
        <CardContent>That's it! You've reached the end for today.</CardContent>
      </Card>
    );
  }
  const definitions: any = nextWord.definitions;
  const results = definitions?.results || [];
  const pronunciation = definitions?.pronunciation?.all;
  return (
    <Card>
      {isAuthStateLoaded && !authenticated ? <NotLoggedInCTA /> : null}
      <Card variant="outlined" style={centeredCard}>
        <CardContent>
          <Typography gutterBottom></Typography>
          <Typography variant="h5" component="div">
            {nextWord.word}
          </Typography>
          {isReviewing === false ? (
            <BackFlashcardContent
              results={results}
              pronunciation={pronunciation}
            />
          ) : null}
        </CardContent>
        <CardActions>
          {isReviewing === true ? (
            <div>
              <MuiButton
                color="primary"
                size="small"
                onClick={() => rateNextWord(5)}
              >
                Easy
              </MuiButton>
              <MuiButton
                color="primary"
                size="small"
                onClick={() => rateNextWord(3)}
              >
                Medium
              </MuiButton>
              <MuiButton
                color="primary"
                size="small"
                onClick={() => rateNextWord(0)}
              >
                Difficult
              </MuiButton>
            </div>
          ) : (
            <MuiButton
              color="primary"
              onClick={() => {
                setIsReviewing(true);
                fetchNextWord(
                  setNextWord,
                  setNextWordLoaded,
                  reviewSession,
                  notify
                );
              }}
            >
              Next
            </MuiButton>
          )}
        </CardActions>
      </Card>
    </Card>
  );
};
